/* eslint-disable no-restricted-syntax -- amber tokens for warning states, theme has no warning colour */
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, ExternalLink, Loader2 } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import type { PlatformSpec } from '@utils/types/connections/platforms.ts';
import type { AuthErrorKind, WizardState } from './types';

const WARNING_ICON = '#B45309';
const WARNING_BORDER = '#F59E0B55';

interface Props {
  platform: PlatformSpec;
  state: WizardState;
  onLaunchOAuth: () => void;
  onCredentialChange: (key: string, value: string) => void;
}

const ERROR_MESSAGE_KEYS: Record<AuthErrorKind, string> = {
  cancelled: 'publishing-authorize-error-cancelled',
  denied: 'publishing-authorize-error-denied',
  popup_blocked: 'publishing-authorize-error-popup-blocked',
  expired: 'publishing-authorize-error-expired',
  unknown: 'publishing-authorize-error-unknown',
  credentials_invalid: 'publishing-authorize-error-credentials'
};

export default function StepAuthorize({
  platform,
  state,
  onLaunchOAuth,
  onCredentialChange
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  if (platform.authType === 'oauth') {
    const succeeded = !!state.oauthCode;
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm" style={{ color: getColour('inactiveText') }}>
          {t('publishing-wizard-authorize-oauth-help', {
            platform: platform.displayName
          })}
        </p>

        {succeeded ? (
          <div
            role="status"
            className="flex items-center gap-3 rounded-xl border p-4"
            style={{
              borderColor: getColour('hairline'),
              background: getColour('secondLayer')
            }}
          >
            <CheckCircle2
              className="h-5 w-5 shrink-0"
              aria-hidden="true"
              style={{ color: getColour('success') }}
            />
            <p className="text-sm font-medium">
              {t('publishing-wizard-authorize-oauth-success', {
                platform: platform.displayName
              })}
            </p>
          </div>
        ) : (
          /* The shared Button hides its label below 1080px when it
             has an icon (editor-toolbar rule). That collapses this CTA
             to an empty icon strip inside the connect-wizard modal, so
             we render a plain button here where the label always shows
             and the width hugs its content. */
          <button
            type="button"
            onClick={onLaunchOAuth}
            disabled={state.isAuthorizing}
            className="inline-flex items-center gap-2 self-start rounded-full px-4 py-2 text-sm font-semibold outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: getColour('activeText'),
              color: getColour('firstLayer')
            }}
          >
            {state.isAuthorizing ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
            )}
            <span>
              {state.isAuthorizing
                ? t('publishing-wizard-authorize-opening')
                : t('publishing-wizard-authorize-oauth-cta', {
                    platform: platform.displayName
                  })}
            </span>
          </button>
        )}

        {state.authError && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-xl border p-4"
            style={{
              borderColor: WARNING_BORDER,
              background: getColour('thirdLayer')
            }}
          >
            <AlertCircle
              className="h-5 w-5 shrink-0"
              aria-hidden="true"
              style={{ color: WARNING_ICON }}
            />
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">
                {t(ERROR_MESSAGE_KEYS[state.authError])}
              </p>
              <Button
                variant="smoke"
                size="small"
                label={t('publishing-wizard-authorize-retry')}
                onClick={onLaunchOAuth}
                disabled={state.isAuthorizing}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  const fields = platform.credentialFields ?? [];

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm" style={{ color: getColour('inactiveText') }}>
        {t('publishing-wizard-authorize-credentials-help', {
          platform: platform.displayName
        })}
      </p>
      <div className="flex flex-col gap-3">
        {fields.map(field => {
          const inputId = `publishing-credential-${field.key}`;
          const value = state.credentials[field.key] ?? '';
          return (
            <div key={field.key} className="flex flex-col gap-1">
              <label
                htmlFor={inputId}
                className="text-sm font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t(field.labelKey)}
                {field.required && (
                  <span aria-hidden="true" className="ml-1 text-red-600">
                    *
                  </span>
                )}
              </label>
              <input
                id={inputId}
                type={field.type === 'password' ? 'password' : 'text'}
                inputMode={field.type === 'url' ? 'url' : undefined}
                autoComplete="off"
                spellCheck={false}
                value={value}
                onChange={e => onCredentialChange(field.key, e.target.value)}
                aria-required={field.required ? true : undefined}
                className="rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
                style={{
                  borderColor: getColour('inputBorder'),
                  background: getColour('firstLayer'),
                  color: getColour('normalText')
                }}
              />
              {field.helpKey && (
                <p
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t(field.helpKey)}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {state.authError === 'credentials_invalid' && (
        <div role="alert" className="text-sm" style={{ color: WARNING_ICON }}>
          {t(ERROR_MESSAGE_KEYS.credentials_invalid)}
        </div>
      )}
    </div>
  );
}
